import { CheckMark, Copy, Cross } from '@o2/icons';
import { IconButton, IconButtonProps, Tooltip } from '@o2/ui';
import { FC, MouseEventHandler, ReactNode, useEffect, useState } from 'react';

export interface CopyButtonProps extends Omit<IconButtonProps, 'onClick'> {
  onClick: () => Promise<CopyStatus> | CopyStatus;
  successIcon?: ReactNode;
  errorIcon?: ReactNode;
  tooltip?: ReactNode;
}

export enum CopyStatus {
  DEFAULT,
  SUCCESS,
  ERROR,
}

const defaultSuccessIcon = <CheckMark />;
const defaultErrorIcon = <Cross />;

const CopyButton: FC<CopyButtonProps> = ({
  color = 'internal',
  variant = 'squared',
  onClick,
  successIcon = defaultSuccessIcon,
  errorIcon = defaultErrorIcon,
  tooltip,
  ...props
}) => {
  const [mode, setMode] = useState<CopyStatus>(CopyStatus.DEFAULT);

  useEffect(() => {
    const timeOut = setTimeout(() => setMode(CopyStatus.DEFAULT), 3000);

    return () => clearTimeout(timeOut);
  }, [mode]);

  const handleCopy: MouseEventHandler<HTMLButtonElement> = async () => {
    const status = await onClick();

    if (status === CopyStatus.SUCCESS || status === CopyStatus.ERROR) {
      setMode(status);
    }
  };

  const getIcon = (): ReactNode => {
    switch (mode) {
      case CopyStatus.SUCCESS:
        return successIcon;
      case CopyStatus.ERROR:
        return errorIcon;
      default:
        return <Copy />;
    }
  };

  const getColor = (): IconButtonProps['color'] => {
    switch (mode) {
      case CopyStatus.SUCCESS:
        return 'success';
      case CopyStatus.ERROR:
        return 'error';
      default:
        return color;
    }
  };

  const btn = (
    <IconButton onClick={handleCopy} color={getColor()} variant={variant} {...props}>
      {getIcon()}
    </IconButton>
  );

  if (tooltip) {
    return <Tooltip title={tooltip}>{btn}</Tooltip>;
  }

  return btn;
};

export default CopyButton;
